import { z } from "zod/v4";
import { defineResource, defineEndpoint } from "@visioni/a3s-client-api";
import { FEATURE_NAME } from "../constants";
import { ReportType, TypePath } from "./report-types";

/**
 * Re-enumerate + regenerate the tenant's most-recent report **from live data**.
 *
 * Unlike `regenerate-report.ts` (which replays the frozen `job.json` verbatim),
 * this rebuilds the `RoadsenseJob` from the current `ScenarioStatus` +
 * eligibility, so changes made since the last daily tick
 * (exclusions, geolocation, direction) take effect immediately rather than
 * waiting for the next enumeration. It is keyed on the report *type* only — the
 * handler resolves which specific report to rebuild (the most-recent ready one)
 * — because the operator triggering it from the config page knows the tenant and
 * type, not a `reportId`/`periodStart`.
 *
 * Empty body: the identity is fully in the URL (`{type}`) and the tenant comes
 * from the scoped path prefix. POST still requires a body schema, so we accept
 * — and ignore — an empty object (mirrors `RegenerateReportBody`).
 */
export const RegenerateRoadsenseLatestBody = z.object({});
export type RegenerateRoadsenseLatestBody = z.output<typeof RegenerateRoadsenseLatestBody>;

/**
 * Fire-and-forget acknowledgement. Regeneration is asynchronous: the endpoint
 * enqueues the freshly-built job onto the RoadSense generator queue and returns
 * immediately (HTTP 200) with `status: "generating"`. `reportId` is echoed so
 * the caller can deep-link / poll the rebuilt report; it is stable across the
 * rebuild because the generator upserts on `(tenant, type, lifecycleStart)` with
 * a write-once `reportId`.
 */
export const RegenerateRoadsenseLatestResponse = z.object({
  data: z.object({
    reportId: z.string(),
    type: ReportType,
    periodStart: z.string(),
    status: z.literal("generating"),
  }),
});
export type RegenerateRoadsenseLatestResponse = z.output<typeof RegenerateRoadsenseLatestResponse>;

/**
 * `POST /v2/tenants/{tenantId}/features/reports/{type}/regenerate-latest`.
 *
 * Tenant-scoped (the path prefix carries `{tenantId}`); the `{type}` segment is
 * validated as a `ReportType` at the edge. Only meaningful for `roadsense` today
 * (cumulative reports rebuilt from live scenario config) — the handler is the
 * one that reads the live `ScenarioStatus`/eligibility data.
 */
export const regenerateRoadsenseLatestResource = defineResource({
  feature: FEATURE_NAME,
  resource: "{type}/regenerate-latest",
  resourcePathSchema: TypePath,
  scope: "tenant",
});

export const regenerateRoadsenseLatestEndpoint = defineEndpoint(regenerateRoadsenseLatestResource, {
  method: "POST",
  bodySchema: RegenerateRoadsenseLatestBody,
  responseSchema: RegenerateRoadsenseLatestResponse,
});
